<script lang="ts" setup>
import { useDark } from '@vueuse/core'
import { useRoute } from 'vue-router'
import { computed, onErrorCaptured, onMounted, onUnmounted, provide, ref, shallowRef, watch } from 'vue'
import { AnimatePresence, motion } from 'motion-v'
import ChangePasswordModal from './components/resource/ChangePasswordModal.vue'
import MandatorySignatureModal from '@/components/resource/signatureCampaign/MandatorySignatureModal.vue'
import NavigationDrawer from '@/components/navigationDrawer/navigationDrawer.vue'
import BAlert from '@/components/common/BAlert.vue'
import BButton from '@/components/common/bButton.vue'
import { isCurrentJobActive } from '@/services/resource'
import { useUserStore } from '@/config/store/userStore'
import { useApplicationStore } from '@/config/store/applicationStore'
import { firmApi, resourceApi, ticketApi, messageApi } from '@/services/api'
import { isModuleActive, setFirmThemeColor } from '@/services/application'
import { applyUserGroupLabels } from '@/services/application/userGroupLabel'
import { useI18n } from 'vue-i18n'
import SearchCommand from './components/application/searchCommand/SearchCommand.vue'
import SwitchFirm from '@/components/application/SwitchFirm.vue'
import TimeZoneRulesWarning from '@/components/application/TimeZoneRulesWarning.vue'
import { canAccessAdministrationMail } from '@/config/router/navigationsGuards/guardAdministration'
import { canUserAccessChat } from '@/config/router/navigationsGuards/guardChat'
import { canUseAiAssistant } from '@/config/router/navigationsGuards/guardAiAssistant'
import { useChatStore } from '@/config/store/chatStore'
import FloatingChatWidget from '@/components/messaging/FloatingChatWidget.vue'
import { useOrbitCompletionWatch } from '@/composables/salesFunnel/useOrbitCompletionWatch'
import PhTicketLight from '~icons/ph/ticket-light'
import MaterialSymbolsMailOutlineRounded from '~icons/material-symbols/mail-outline-rounded'
import MaterialSymbolsChatOutlineRounded from '~icons/material-symbols/chat-outline-rounded'
import PhSparkle from '~icons/ph/sparkle'
import { SSO_ENTRY_POINT } from '@/config/constantes'
import { storeToRefs } from 'pinia'
const { t, locale } = useI18n()

const userStore = useUserStore()
locale.value = userStore?.user?.user?.lang?.toLowerCase() || locale.value // get the locale from the user store or use the default locale 
useDark()
const route = useRoute()
// A page is rebuilt when its path changes, unless its route keeps it: `meta.viewKey(route)` (tabs
// in the path of a page holding an unsaved draft, e.g. the space of an e-learning course).
const routeViewKey = computed(() => {
  const viewKey = route.meta.viewKey
  return typeof viewKey === 'function' ? String(viewKey(route)) : route.path
})
const isOpenChangePasswordModal = ref(false)

const changeModalComputed = computed({
  get: () => {
    if (!userStore.user)
      return false  
    return isOpenChangePasswordModal.value || (userStore.user?.user?.toChangePassword && !SSO_ENTRY_POINT)
  },
  set: (value) => {
    isOpenChangePasswordModal.value = value
  },
})

const isGlobalAdminUser = computed(() => {
  return userStore.user?.role === 'admin' && !userStore.user?._firmId
})

const hasActiveCurrentJob = computed(() => {
  if (!userStore.user?.currentJob)
    return false

  return isCurrentJobActive(userStore.user.currentJob)
})

const shouldShowNavigationDrawer = computed(() => {
  return isGlobalAdminUser.value || hasActiveCurrentJob.value
})

const shouldShowTopNavigation = computed(() => {
  return hasActiveCurrentJob.value
})

const scrollBlocked = ref(false)

provide('scrollBlocked', scrollBlocked)

const scrollStatus = computed(() => {
  if (scrollBlocked.value) {
    return '--root-scroll-page: 100vh'
  } else {
    return '--root-scroll-page: auto'
  }
})

watch(scrollBlocked, (value) => {
  let root = document.querySelector(':root');
  if (value) {
    root.style.setProperty('--root-scroll-page', '100vh')
  } else {
    root.style.setProperty('--root-scroll-page', 'auto')
  }
})

const applicationStore = useApplicationStore()

const refreshUserContext = async () => {
  if (!userStore.user)
    return

  // Apply persisted firm theme immediately to avoid a flash of default colors
  // while waiting for getConnectedResource / firmContext.
  if (userStore.user.firm) {
    setFirmThemeColor(userStore.user.firm)
    applyUserGroupLabels(userStore.user.firm)
  }

  try {
    const [resource, firmContext] = await Promise.all([
      resourceApi.getConnectedResource().promise,
      userStore.user._firmId ? firmApi.firmContext().promise : Promise.resolve({ offices: [], countries: [] }),
    ])

    userStore.user = resource
    userStore.setFirmContext(firmContext)
    locale.value = userStore.user.user?.lang?.toLowerCase() || locale.value
    if (userStore.user.firm) {
      setFirmThemeColor(userStore.user.firm)
      applyUserGroupLabels(userStore.user.firm)
    }
  }
  catch (err) {
    console.log('refresh user error', err)
  }
}

watch([() => userStore.user?._id, () => userStore.user?._firmId], () => {
  if (!userStore.user) {
    userStore.setFirmContext()
    return
  }

  refreshUserContext()
}, { immediate: true })

const removeError = (error) => {
  applicationStore.removeError(error)
}

const canAccessTickets = computed(() => {
  return !!(userStore.token && userStore.user?.profile?.modules?.ticket?.isAccess)
})

const isAuthenticated = computed(() => !!(userStore.token && userStore.user?._id))

const canAccessChat = computed(() => isAuthenticated.value && canUserAccessChat(userStore.user))
const canAccessAskAi = computed(() => isAuthenticated.value && canUseAiAssistant())

const chatStore = useChatStore()
const { chatDotConfig } = storeToRefs(chatStore)
useOrbitCompletionWatch()

// Inbox access - tied to recruitment for now
const canAccessInbox = computed(() => {
  if (!isAuthenticated.value || !userStore.user?.firm) return false
  const hasRecruitmentAccess = isModuleActive(userStore.user.firm, 4) && userStore.user.profile?.modules?.recruitment?.isAccess
  return hasRecruitmentAccess
})

const canAccessAdministrationMailNav = computed(() =>
  isAuthenticated.value && canAccessAdministrationMail(userStore.user),
)

// Inbox unread count
const inboxUnreadCount = ref(0)
let inboxUnreadCountInterval: ReturnType<typeof setInterval> | null = null

const fetchInboxUnreadCount = async () => {
  if (!isAuthenticated.value || !canAccessInbox.value) return
  try {
    const response = await messageApi.getUnreadCount().promise
    inboxUnreadCount.value = response.unreadCount
  }
  catch (err) {
    console.error('Error fetching inbox unread count:', err)
  }
}

const inboxDotConfig = computed(() => {
  if (inboxUnreadCount.value === 0) return false
  return {
    content: inboxUnreadCount.value > 99 ? '99+' : String(inboxUnreadCount.value),
    bg: 'error' as const,
  }
})

const bouncedMailCount = ref(0)
let bouncedMailCountInterval: ReturnType<typeof setInterval> | null = null

const fetchBouncedMailCount = async () => {
  if (!isAuthenticated.value || !canAccessAdministrationMailNav.value || !userStore.user?._firmId) return
  try {
    const response = await messageApi.getBouncedCount(userStore.user._firmId).promise
    bouncedMailCount.value = response.count
  }
  catch (err) {
    console.error('Error fetching bounced mail count:', err)
  }
}

const administrationMailDotConfig = computed(() => {
  if (bouncedMailCount.value === 0) return false
  return {
    content: bouncedMailCount.value > 99 ? '99+' : String(bouncedMailCount.value),
    bg: 'error' as const,
  }
})

// Ticket unseen count
const unseenCount = ref(0)
let unseenCountInterval: ReturnType<typeof setInterval> | null = null

const fetchUnseenCount = async () => {
  if (!isAuthenticated.value || !canAccessTickets.value) return
  try {
    const response = await ticketApi.getUnseenCount().promise
    unseenCount.value = response.unseenCount
  }
  catch (err) {
    console.error('Error fetching unseen count:', err)
  }
}

const ticketDotConfig = computed(() => {
  if (unseenCount.value === 0) return false
  return {
    content: unseenCount.value > 99 ? '99+' : String(unseenCount.value),
    bg: 'error' as const,
  }
})

const stopBadgePolling = () => {
  if (unseenCountInterval) {
    clearInterval(unseenCountInterval)
    unseenCountInterval = null
  }
  if (inboxUnreadCountInterval) {
    clearInterval(inboxUnreadCountInterval)
    inboxUnreadCountInterval = null
  }
  if (bouncedMailCountInterval) {
    clearInterval(bouncedMailCountInterval)
    bouncedMailCountInterval = null
  }
  chatStore.stop()
}

const startBadgePolling = () => {
  if (!isAuthenticated.value) return

  fetchUnseenCount()
  unseenCountInterval = setInterval(fetchUnseenCount, 600000) // Poll every 10 minutes
  fetchInboxUnreadCount()
  inboxUnreadCountInterval = setInterval(fetchInboxUnreadCount, 600000) // Poll every 10 minutes
  fetchBouncedMailCount()
  bouncedMailCountInterval = setInterval(fetchBouncedMailCount, 600000)
  if (canAccessChat.value) {
    chatStore.start()
  }
}

watch(isAuthenticated, (connected) => {
  stopBadgePolling()
  if (connected) startBadgePolling()
}, { immediate: true })

watch(canAccessChat, (access) => {
  if (!isAuthenticated.value || !access) {
    chatStore.stop()
    return
  }
  chatStore.start()
})

onUnmounted(() => {
  stopBadgePolling()
})

const updatedErrors = computed(() => {
  return applicationStore.errors.map((error) => {
    return {
      ...error,
      title: error.translatedTitle ? t(error.translatedTitle, error.translatedParams ?? {}) : error.title,
      message: error.translatedMessage ? t(error.translatedMessage, error.translatedParams ?? {}) : error.message,
    }
  })
})

if (import.meta.env.DEV) {
  watch(() => ({ path: route.path, fullPath: route.fullPath, name: route.name }), (to, from) => {
    if (!to.path.startsWith('/super-admin') && !(from?.path || '').startsWith('/super-admin')) return
    if (to.path === from?.path && to.name === from?.name) return

    console.debug('[super-admin-route-transition]', {
      from: from?.fullPath || '',
      to: to.fullPath,
      routeName: to.name,
    })
  })

  onErrorCaptured((error, instance, info) => {
    // A public instance has no `type`: reading it warns on every render.
    const componentName = instance?.$options?.name || instance?.$options?.__name || 'anonymous'
    console.error('[route-error]', {
      path: route.fullPath,
      routeName: route.name,
      componentName,
      info,
      error,
    })

    return false
  })
}

</script>

<template>
  <div class="relative transition-all flex flex-col w-screen min-h-screen"
    :class="[shouldShowNavigationDrawer ? !applicationStore.drawer.fixed ? 'md:pl-14' : 'md:pl-64' : undefined]"
    :style="[scrollStatus]">
    <ChangePasswordModal v-model:is-open="changeModalComputed" />
    <MandatorySignatureModal
      v-if="isAuthenticated && userStore.user?.firm?.eSignature"
      :paused="changeModalComputed"
    />
    <div class="flex flex-wrap">
      <div v-if="shouldShowTopNavigation"
        class="bg-base p-1 w-full rounded-none h-12 print:hidden hidden md:flex items-center justify-between gap-3 px-3 min-w-0">

        <div class="min-w-0 flex-1 max-w-3xl">
          <SearchCommand />
        </div>

        <div class="flex items-center gap-2 shrink-0">
          <BButton
            v-if="canAccessAskAi"
            bg="base"
            size="small"
            :title="t('common.drawer.askAi')"
            :to="{ name: 'AskAi' }"
          >
            <PhSparkle class="w-5 h-5 shrink-0" />
            <span class="ml-1.5 hidden xl:inline whitespace-nowrap">{{ t('common.drawer.askAi') }}</span>
          </BButton>
          <BButton
            v-if="canAccessAdministrationMailNav"
            bg="base"
            size="small"
            :dot="administrationMailDotConfig"
            :title="t('common.drawer.administrationMail')"
            :to="{ name: 'AdministrationMail' }"
          >
            <MaterialSymbolsMailOutlineRounded class="w-5 h-5 shrink-0" />
            <span class="ml-1.5 hidden xl:inline whitespace-nowrap">{{ t('common.drawer.administrationMail') }}</span>
          </BButton>
          <BButton
            v-if="canAccessChat"
            bg="base"
            size="small"
            :dot="chatDotConfig"
            :title="t('common.drawer.messaging')"
            :to="{ name: 'Messaging' }"
          >
            <MaterialSymbolsChatOutlineRounded class="w-5 h-5 shrink-0" />
            <span class="ml-1.5 hidden xl:inline whitespace-nowrap">{{ t('common.drawer.messaging') }}</span>
          </BButton>
          <BButton
            v-if="canAccessInbox"
            bg="base"
            size="small"
            :dot="inboxDotConfig"
            :title="t('common.drawer.inbox')"
            :to="{name: 'Inbox'}"
          >
            <MaterialSymbolsMailOutlineRounded class="w-5 h-5 shrink-0" />
            <span class="ml-1.5 hidden xl:inline whitespace-nowrap">{{ t('common.drawer.inbox') }}</span>
          </BButton>
          <BButton
            v-if="canAccessTickets"
            bg="base"
            size="small"
            :dot="ticketDotConfig"
            :title="t('common.drawer.ticket')"
            :to="{name: 'Tickets'}"
          >
            <PhTicketLight class="w-5 h-5 shrink-0" />
            <span class="ml-1.5 hidden xl:inline whitespace-nowrap">{{ t('common.drawer.ticket') }}</span>
          </BButton>
        </div>

      </div>
      <div v-if="!shouldShowNavigationDrawer && userStore.user && !userStore.user?.currentJob?._id && (userStore.user?.firmList?.length ?? 0) > 1"
        class="bg-base p-1 w-full rounded-none h-12 flex items-center justify-between">
        <SwitchFirm />

      </div>
      <NavigationDrawer v-if="shouldShowNavigationDrawer" class="print:hidden" @change-password="changeModalComputed = true" />
    </div>
    <TimeZoneRulesWarning v-if="isAuthenticated" />
    <router-view v-slot="{ Component }">
      <template v-if="Component">
        <transition name="fade" mode="out-in">
          <div :key="routeViewKey" class="route-view-shell min-h-0">
            <Suspense timeout="0">
              <template #default>
                <component :is="Component" />
              </template>
              <template #fallback>
                <div class="min-h-[calc(100vh-3rem)]" />
              </template>
            </Suspense>
          </div>
        </transition>
      </template>
    </router-view>

    <Teleport to="body">
      <div class="toast-stack fixed bottom-6 right-6 z-100 flex flex-col gap-3 max-w-md w-full">
        <AnimatePresence>
          <motion.div 
            v-for="error in updatedErrors" 
            :key="error.id"
            :initial="{ opacity: 0, x: 100, scale: 0.9 }"
            :animate="{ opacity: 1, x: 0, scale: 1 }"
            :exit="{ opacity: 0, x: 100, scale: 0.9 }"
            :transition="{ type: 'spring', stiffness: 400, damping: 25 }"
          >
            <BAlert
              :alert="error"
              shadow
              size="normal"
              @close="removeError"
            />
          </motion.div>
        </AnimatePresence>
      </div>
    </Teleport>

    <FloatingChatWidget />
  </div>
</template>

<style>
/* Toasts stay above modals, except under an open confirmation (BConfirm), whose buttons they would cover */
body:has([role="alertdialog"]) .toast-stack {
  z-index: 60;
}
</style>
